// In vận đơn (01/11/2026) — nút In đứng cạnh mọi nút Tải vận đơn, in thẳng PDF từ máy chủ ShopTrack.
// Thay cách cũ (POST /shipping/print → mở trang PDF của hãng, nhiều tài khoản thì nhiều thông báo).

import { toast } from 'sonner'
import { fetchFile } from '@/lib/api'

// Cùng file với nút Tải (POST /shipping/waybills/download, `print: true` → luôn một PDF, đúng thứ tự).
// Máy tính: PDF nạp vào khung ẩn rồi mở hộp in của máy. Điện thoại (data-viewport="mobile"): khung ẩn không in
// được → mở PDF ở thẻ mới (mở trước khi chờ máy chủ, không thì trình duyệt chặn cửa sổ bật lên), in / chia sẻ từ đó.

/** Vận đơn tối đa trong một PDF in — khớp WAYBILLS_PER_PRINT của máy chủ. Nhiều hơn → chia phần. */
export const PRINT_BATCH = 500

const isPhone = () => document.documentElement.getAttribute('data-viewport') === 'mobile'

let frame: HTMLIFrameElement | null = null

function printInFrame(url: string) {
  frame?.remove()
  const f = document.createElement('iframe')
  f.setAttribute('aria-hidden', 'true')
  f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
  f.onload = () => {
    try {
      f.contentWindow?.focus()
      f.contentWindow?.print()
    } catch {
      window.open(url, '_blank', 'noopener')
    }
  }
  f.src = url
  document.body.appendChild(f)
  frame = f
}

/** Cửa sổ chờ cho điện thoại — mở ngay lúc bấm. */
function openPending(): Window | null {
  const w = window.open('', '_blank')
  if (w) w.document.title = 'Đang chuẩn bị bản in…'
  return w
}

async function printPart(codes: string[], pending: Window | null) {
  const blob = await fetchFile('/shipping/waybills/download', { codes, print: true })
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
  // Giữ lâu: hộp in / thẻ PDF còn đọc file sau khi hàm này xong
  setTimeout(() => URL.revokeObjectURL(url), 10 * 60_000)
  if (!isPhone()) return printInFrame(url)
  if (pending && !pending.closed) pending.location.href = url
  else toast('Bản in vận đơn đã sẵn sàng', { duration: 60_000, action: { label: 'Mở', onClick: () => window.open(url, '_blank') } })
}

/**
 * In vận đơn — `codes` theo đúng thứ tự cần in (hoặc hàm lấy danh sách, ví dụ theo khoảng ngày). Quá PRINT_BATCH
 * thì in phần đầu, các phần sau mỗi phần một thông báo có nút "In" (mỗi lần mở hộp in cần người bấm).
 * Trả số vận đơn của phần đã in.
 */
export async function printWaybills(source: string[] | (() => Promise<string[]>)): Promise<number> {
  const pending = isPhone() ? openPending() : null
  try {
    const codes = typeof source === 'function' ? await source() : source
    if (!codes.length) throw new Error('Không có vận đơn để in')
    const parts: string[][] = []
    for (let i = 0; i < codes.length; i += PRINT_BATCH) parts.push(codes.slice(i, i + PRINT_BATCH))
    await printPart(parts[0]!, pending)
    parts.slice(1).forEach((part, i) => {
      const title = `Phần ${i + 2}/${parts.length} · ${part.length} vận đơn`
      toast(title, {
        duration: Infinity,
        action: {
          label: 'In',
          onClick: () => {
            const w = isPhone() ? openPending() : null
            printPart(part, w).catch((err) => {
              w?.close()
              toast.error(title, { description: (err as Error).message })
            })
          },
        },
      })
    })
    return parts[0]!.length
  } catch (err) {
    pending?.close()
    throw err
  }
}
